/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { getGlobalLocale } from "@adobe-commerce/elsie/lib";

/**
 * Gets the user's timezone with fallback to UTC if detection fails
 * @returns The user's timezone string (e.g., 'America/New_York') or 'UTC' as fallback
 */
export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn('Failed to detect user timezone, falling back to UTC:', error);
    return 'UTC';
  }
}

/**
 * Checks if a date string is a valid date
 * @param dateString - The date string to check
 * @returns True if the date string is a valid date, false otherwise
 */
export function isValidDate(dateString: string): boolean {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
}

/**
 * Formats a date string to locale date string, returning "–" for invalid dates
 * @param dateString - The date string to format
 * @returns Formatted date string or "–" for invalid dates
 */
export function formattedDate(dateString: string, type: 'short' | 'long' = 'short'): string {
  if (!isValidDate(dateString)) {
    return '–';
  }

  const locale = getGlobalLocale() || 'en-US';
  
  // For dates in YYYY-MM-DD format (like expiration dates), parse as local date to avoid timezone issues
  // This prevents dates from shifting when displayed in different timezones
  let date: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    // Parse as local date (not UTC) to avoid timezone conversion
    const [year, month, day] = dateString.split('-').map(Number);
    date = new Date(year, month - 1, day);
  } else {
    // For ISO timestamps or other formats, parse normally
    date = new Date(dateString);
  }

  if (type === 'short') {
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
  }

  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}
